import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus, Truck } from "lucide-react";
import ProductVisual, { Sprig } from "../components/ProductVisual";
import ProductCard from "../components/ProductCard";
import MakerStrip from "../components/MakerStrip";
import { Reveal, EASE } from "../components/Reveal";
import { CLINIC, PATCH_TEST_NOTE, formatINR, FREE_DELIVERY_THRESHOLD } from "../data/catalog";
import { fetchProducts } from "../lib/store";
import { useCart } from "../context/CartContext";

const DECLARATIONS = (p) => [
  ["Manufactured & marketed by", `Ayushi Ayurveda Wellness, ${CLINIC.address}`],
  ["Common name of commodity", p.commonName],
  ["Net quantity", p.size],
  ["Retail sale price", `₹${p.price} (inclusive of all taxes)`],
  ["Country of origin", "India"],
  ["Manufacturing & expiry", "Batch number, date of manufacture and best-before printed on the pack"],
  ["Consumer care", `${CLINIC.phone} · ${CLINIC.email} · Monday to Saturday, 10 AM to 7 PM`],
];

const Block = ({ title, children, testid }) => (
  <Reveal className="border-t hairline py-10">
    <h2 className="eyebrow" data-testid={testid}>{title}</h2>
    <div className="mt-5">{children}</div>
  </Reveal>
);

const ProductPage = () => {
  const { slug } = useParams();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const product = all.find((p) => p.slug === slug);
  const [qty, setQtyLocal] = useState(1);
  const [visual, setVisual] = useState(0);
  const [pin, setPin] = useState("");
  const [pinResult, setPinResult] = useState(null);
  const { add } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = await fetchProducts();
        if (alive) setAll(rows);
      } catch (e) {
        console.error("Product load failed:", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    setQtyLocal(1);
    setVisual(0);
    setPin("");
    setPinResult(null);
  }, [slug]);

  const related = useMemo(() => {
    if (!product) return [];
    const entry = all.find((p) => p.entry);
    const sameCat = all.filter((p) => p.category === product.category && p.id !== product.id);
    const picks = [entry, ...sameCat].filter((p) => p && p.id !== product.id);
    return [...new Map(picks.map((p) => [p.id, p])).values()].slice(0, 3);
  }, [product, all]);

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-4 pb-24 pt-44" data-testid="product-loading">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <div className="aspect-[4/5] w-full animate-pulse bg-ink/5" />
          <div className="space-y-4">
            <div className="h-3 w-24 animate-pulse bg-ink/5" />
            <div className="h-10 w-3/4 animate-pulse bg-ink/5" />
            <div className="h-6 w-28 animate-pulse bg-ink/5" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-7xl px-4 pb-24 pt-44 text-center">
        <p className="font-serif text-3xl italic">This page doesn't exist.</p>
        <Link to="/shop" className="mt-6 inline-block border-b border-ink/40 pb-1 text-[12px] uppercase tracking-[0.2em]">
          Back to the shop
        </Link>
      </div>
    );
  }

  const galleryCount = Math.max(product.images.length, 1);

  const checkPin = () => {
    if (/^\d{6}$/.test(pin.trim())) {
      setPinResult({ ok: true, text: `Delivery to ${pin.trim()} in 3 to 7 working days.` });
    } else {
      setPinResult({ ok: false, text: "That doesn't look like a valid pincode — please check it." });
    }
  };

  return (
    <div className="pt-32 md:pt-40" data-testid="product-page">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <Link to="/shop" data-testid="back-to-shop" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-ink/55 transition-colors hover:text-ink">
          <ArrowLeft className="h-3.5 w-3.5" /> All products
        </Link>

        <div className="mt-8 grid gap-12 pb-8 lg:grid-cols-2 lg:gap-16">
          <motion.div key={slug} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }}>
            <ProductVisual product={product} variant={visual} className="aspect-[4/5] w-full" />
            {galleryCount > 1 && (
              <div className="mt-4 flex gap-3" data-testid="product-gallery">
                {[...Array(galleryCount)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setVisual(i)}
                    data-testid={`gallery-thumb-${i}`}
                    aria-label={`View ${i + 1}`}
                    className={`overflow-hidden border transition-colors duration-300 ${visual === i ? "border-ink" : "hairline"}`}
                  >
                    <ProductVisual product={product} variant={i} className="h-20 w-16" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div key={`${slug}-info`} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1, ease: EASE }}>
            <div className="flex items-center gap-3">
              <p className="eyebrow" data-testid="product-category">{product.category}</p>
              {product.badge && (
                <span className="border border-sage-deep/40 px-2.5 py-1 text-[9px] uppercase tracking-[0.18em] text-sage-deep" data-testid="product-badge">{product.badge}</span>
              )}
            </div>
            <h1 className="mt-4 font-serif text-4xl font-light leading-tight md:text-5xl" data-testid="product-name">{product.name}</h1>
            <p className="mt-3 text-sm text-ink/55">{product.size}</p>
            <p className="mt-5 font-serif text-3xl" data-testid="product-price">
              {formatINR(product.price)}
              <span className="ml-3 align-middle font-sans text-[11px] tracking-[0.08em] text-ink/40">inclusive of all taxes</span>
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <div className="flex items-center border hairline" data-testid="quantity-stepper">
                <button onClick={() => setQtyLocal((q) => Math.max(1, q - 1))} data-testid="qty-decrease" aria-label="Decrease quantity" className="flex h-12 w-12 items-center justify-center transition-colors hover:bg-cream-2">
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-10 text-center tabular-nums" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQtyLocal((q) => q + 1)} data-testid="qty-increase" aria-label="Increase quantity" className="flex h-12 w-12 items-center justify-center transition-colors hover:bg-cream-2">
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <button
                onClick={() => add(product.id, qty)}
                data-testid="add-to-cart-button"
                className="rounded-full bg-ink px-10 py-4 text-[13px] uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-ink-deep"
              >
                Add to Cart
              </button>
              <button
                onClick={() => {
                  add(product.id, qty, true);
                  navigate("/checkout");
                }}
                data-testid="buy-now-button"
                className="rounded-full border border-ink/30 px-10 py-4 text-[13px] uppercase tracking-[0.18em] transition-colors duration-300 hover:border-ink"
              >
                Buy Now
              </button>
            </div>

            <div className="mt-6 flex items-end gap-3">
              <label className="flex-1">
                <span className="text-[11px] uppercase tracking-[0.22em] text-ink/55">Delivery estimate</span>
                <input
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="Enter pincode"
                  inputMode="numeric"
                  data-testid="pincode-input"
                  className="mt-2 w-full border hairline bg-white/70 px-4 py-3 text-[15px] placeholder:text-ink/30"
                />
              </label>
              <button onClick={checkPin} data-testid="pincode-check" className="border hairline px-6 py-3 text-[12px] uppercase tracking-[0.18em] transition-colors hover:bg-cream-2">
                Check
              </button>
            </div>
            {pinResult && (
              <p className={`mt-3 inline-flex items-center gap-2 text-[13px] ${pinResult.ok ? "text-sage-deep" : "text-ink/60"}`} data-testid="pincode-result">
                <Truck className="h-4 w-4" /> {pinResult.text}
              </p>
            )}
            <p className="mt-4 text-[12px] text-ink/55">
              Free delivery across India on orders above {formatINR(FREE_DELIVERY_THRESHOLD)}.
            </p>

            <p className="mt-8 border-t hairline pt-8 text-[15px] leading-relaxed text-ink/75" data-testid="product-short-desc">
              {product.short}
            </p>
          </motion.div>
        </div>

        <div className="grid gap-x-16 pb-16 lg:grid-cols-2">
          <div>
            {product.keyIngredients.length > 0 && (
            <Block title="Key ingredients" testid="key-ingredients">
              <ul className="space-y-4">
                {product.keyIngredients.map((k) => (
                  <li key={k.name} className="flex items-start gap-4">
                    <Sprig className="mt-1 h-5 w-auto shrink-0 text-sage-deep" />
                    <p className="text-[15px] text-ink/80">
                      <span className="font-medium">{k.name}.</span>{" "}
                      <span className="text-ink/60">{k.line}</span>
                    </p>
                  </li>
                ))}
              </ul>
            </Block>
            )}
            {product.howToUse.length > 0 && (
            <Block title="How to use" testid="how-to-use">
              <ol className="space-y-4">
                {product.howToUse.map((s, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span className="font-serif text-lg italic text-sage-deep">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-[15px] leading-relaxed text-ink/75">{s}</p>
                  </li>
                ))}
              </ol>
            </Block>
            )}
            {product.fullIngredients && (
            <Block title="Full ingredient list" testid="full-ingredients">
              <p className="text-[15px] leading-relaxed text-ink/75">{product.fullIngredients}</p>
            </Block>
            )}
          </div>
          <div>
            <Block title="Mandatory declarations" testid="declarations">
              <dl className="divide-y divide-ink/10 border hairline">
                {DECLARATIONS(product).map(([k, v]) => (
                  <div key={k} className="grid gap-1 px-5 py-3.5 sm:grid-cols-5">
                    <dt className="text-[11px] uppercase tracking-[0.16em] text-ink/45 sm:col-span-2">{k}</dt>
                    <dd className="text-[13px] leading-relaxed text-ink/80 sm:col-span-3">{v}</dd>
                  </div>
                ))}
              </dl>
            </Block>
            <Block title="Before you use it" testid="patch-test-note">
              <p className="border-l-2 border-sage-deep/50 pl-5 text-[14px] italic leading-relaxed text-ink/70">
                {PATCH_TEST_NOTE}
              </p>
            </Block>
            <div className="border-t hairline py-10">
              <MakerStrip />
            </div>
            <Block title="Reviews" testid="reviews-block">
              <p className="text-[14px] leading-relaxed text-ink/55" data-testid="reviews-empty">
                No reviews yet. Only verified purchases can leave one — when the first ones arrive,
                they will appear here, unedited.
              </p>
            </Block>
          </div>
        </div>

        {related.length > 0 && (
          <section className="border-t hairline py-16 md:py-20" data-testid="related-products">
            <Reveal>
              <h2 className="font-serif text-3xl font-light italic md:text-4xl">You may also like</h2>
            </Reveal>
            <div className="mt-10 grid grid-cols-2 gap-x-4 gap-y-10 md:gap-x-6 lg:grid-cols-3">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProductPage;
